import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Check, Maximize, Minimize, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { BatSignalOverlay, BatSignalTile } from "@/components/panel/BatSignal";
import { PunchDialog } from "@/components/panel/PunchDialog";
import { VacationRequestDialog } from "@/components/panel/VacationRequestDialog";
import { EmployeeAvatar } from "@/components/timeclock/EmployeeAvatar";
import { LiveClock } from "@/components/timeclock/LiveClock";
import { StatusChip } from "@/components/timeclock/StatusChip";
import { useFullscreen } from "@/hooks/useFullscreen";
import { useLongPress } from "@/hooks/useLongPress";
import { panelApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatClock, formatWorkDate, localDate, statusOf } from "#shared/timeclock";
import type { Employee, EmployeeBalance, PunchAction, Shift } from "#shared/types";

const FAREWELLS: Record<PunchAction, (name: string) => string> = {
  check_in: (name) => `Välkommen, ${name}!`,
  lunch_start: (name) => `Smaklig måltid, ${name}`,
  lunch_end: (name) => `Välkommen tillbaka, ${name}`,
  check_out: (name) => `Ha en bra kväll, ${name}`,
};

export function PanelPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [selected, setSelected] = useState<Employee | null>(null);
  const [leaveFor, setLeaveFor] = useState<Employee | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [batSignal, setBatSignal] = useState(false);

  const today = localDate();

  // Adminläget har medvetet ingen synlig knapp — håll in klockan i 3 s.
  const adminGesture = useLongPress(() => navigate({ to: "/admin" }));

  const fullscreen = useFullscreen();

  const employeesQuery = useQuery({
    queryKey: ["employees", "active"],
    queryFn: panelApi.employees,
  });

  const shiftsQuery = useQuery({
    queryKey: ["shifts", today],
    queryFn: () => panelApi.shiftsForDate(today),
    refetchInterval: 60_000,
  });

  const shiftByEmployee = useMemo(() => {
    const map = new Map<string, Shift>();
    for (const shift of shiftsQuery.data ?? []) map.set(shift.employee_id, shift);
    return map;
  }, [shiftsQuery.data]);

  // Pass från tidigare dagar som aldrig stämplades ut. Kan bara rättas i
  // adminläget, så panelen varnar för att den anställda ska säga till.
  const openShiftsQuery = useQuery({
    queryKey: ["shifts", "open", today],
    queryFn: () => panelApi.openShiftsBefore(today),
    refetchInterval: 60_000,
  });

  const missedByEmployee = useMemo(() => {
    const map = new Map<string, Shift>();
    // Sorterat stigande från servern, så det senaste glömda passet vinner.
    for (const shift of openShiftsQuery.data ?? []) map.set(shift.employee_id, shift);
    return map;
  }, [openShiftsQuery.data]);

  // Saldona uppdateras bara vid export, så de behöver inte hämtas ofta. Saknad
  // Fortnox-koppling ska inte störa panelen — därför inga omförsök.
  const balancesQuery = useQuery({
    queryKey: ["balances"],
    queryFn: panelApi.balances,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const balanceByNumber = useMemo(() => {
    const map = new Map<string, EmployeeBalance>();
    for (const balance of balancesQuery.data ?? []) map.set(balance.employee_number, balance);
    return map;
  }, [balancesQuery.data]);

  const punch = useMutation({
    mutationFn: ({ employeeId, action }: { employeeId: string; action: PunchAction }) =>
      panelApi.punch(employeeId, action, today),
    onSuccess: (_shift, variables) => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      const firstName = selected?.name.split(" ")[0] ?? "";
      setConfirmation(FAREWELLS[variables.action](firstName));
      setSelected(null);
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      setConfirmation("Något gick fel. Försök igen.");
      setSelected(null);
    },
  });

  useEffect(() => {
    if (!confirmation) return;
    const id = setTimeout(() => setConfirmation(null), 2600);
    return () => clearTimeout(id);
  }, [confirmation]);

  const employees = employeesQuery.data ?? [];

  return (
    <main className="kiosk min-h-screen px-5 py-6 sm:px-8 sm:py-10">
      <header className="relative mx-auto max-w-5xl">
        <div {...adminGesture} className="cursor-default select-none">
          <LiveClock />
        </div>
        {fullscreen.supported && (
          // Absolut placerad för att inte förskjuta klockan, som är centrerad.
          <button
            onClick={() => void fullscreen.toggle()}
            aria-label={fullscreen.active ? "Avsluta helskärm" : "Helskärm"}
            title={fullscreen.active ? "Avsluta helskärm" : "Helskärm"}
            className="press absolute top-0 right-0 rounded-full p-2.5 text-muted-foreground/60 hover:bg-card hover:text-foreground"
          >
            {fullscreen.active ? <Minimize className="size-5" /> : <Maximize className="size-5" />}
          </button>
        )}
      </header>

      <section className="mx-auto mt-9 max-w-5xl">
        {employeesQuery.isLoading ? (
          <p className="py-16 text-center text-muted-foreground">Hämtar personal…</p>
        ) : employeesQuery.isError ? (
          <div className="surface mx-auto max-w-md p-10 text-center">
            <h2 className="text-lg font-semibold">Ingen kontakt med servern</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Panelen kunde inte hämta personallistan. Kontrollera nätverket — den försöker igen
              automatiskt.
            </p>
          </div>
        ) : employees.length === 0 ? (
          <div className="surface mx-auto max-w-md p-10 text-center">
            <h2 className="text-lg font-semibold">Ingen personal upplagd</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Lägg upp personalen i adminläget, så visas de här. Håll in klockan för att komma dit.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {employees.map((employee) => {
              const shift = shiftByEmployee.get(employee.id);
              const status = statusOf(shift);
              const missed = missedByEmployee.get(employee.id);
              return (
                <button
                  key={employee.id}
                  onClick={() => setSelected(employee)}
                  className={cn(
                    "press flex flex-col items-center rounded-3xl bg-card p-5 text-center shadow-tile",
                    status === "working" && "ring-2 ring-success/50",
                    status === "lunch" && "ring-2 ring-warning/60",
                  )}
                >
                  <EmployeeAvatar
                    name={employee.name}
                    photoUrl={employee.photo_url}
                    className="size-24 text-6xl"
                  />
                  <span className="mt-3 line-clamp-1 text-base font-semibold">{employee.name}</span>
                  <StatusChip status={status} className="mt-2" />
                  <span className="tabular mt-2 text-xs text-muted-foreground">
                    {shift?.check_in ? `In ${formatClock(shift.check_in)}` : "—"}
                    {shift?.check_out ? ` · Ut ${formatClock(shift.check_out)}` : ""}
                  </span>
                  {missed && (
                    <span className="mt-1.5 flex items-center gap-1 text-xs font-medium text-destructive">
                      <TriangleAlert className="size-3.5" />
                      Ej utstämplad {formatWorkDate(missed.work_date)}
                    </span>
                  )}
                </button>
              );
            })}
            {/* Påskägg. Ligger utanför employees-listan och är ingen anställd. */}
            <BatSignalTile onSummon={() => setBatSignal(true)} />
          </div>
        )}
      </section>

      <PunchDialog
        employee={selected}
        shift={selected ? shiftByEmployee.get(selected.id) : undefined}
        missedShift={selected ? missedByEmployee.get(selected.id) : undefined}
        balance={
          selected?.employee_number ? balanceByNumber.get(selected.employee_number) : undefined
        }
        pending={punch.isPending}
        onClose={() => setSelected(null)}
        onPunch={(action) => selected && punch.mutate({ employeeId: selected.id, action })}
        onRequestLeave={() => {
          setLeaveFor(selected);
          setSelected(null);
        }}
      />

      <VacationRequestDialog employee={leaveFor} onClose={() => setLeaveFor(null)} />

      {batSignal && <BatSignalOverlay onDone={() => setBatSignal(false)} />}

      {confirmation && (
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-foreground/25 p-6 backdrop-blur-sm">
          <div className="surface animate-in zoom-in-95 flex flex-col items-center px-12 py-10">
            <div className="flex size-20 items-center justify-center rounded-full bg-success text-success-foreground">
              <Check className="size-11" strokeWidth={3} />
            </div>
            <p className="mt-5 text-center text-2xl font-semibold">{confirmation}</p>
          </div>
        </div>
      )}
    </main>
  );
}
